"use client"
 
import { useEffect, useState, useRef } from "react"
import { Link } from "react-router-dom"
import { Calendar, Phone, Search, Filter, User } from "lucide-react"
import SEO from "./SEO"
 
import { Button } from "../components/ui/button"
import { Input } from "../components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select"
 
const WP_API_BASE = "/wp-json/wp/v2"
const INITIAL_LOAD = 100
const LOAD_MORE_COUNT = 12
const PLACEHOLDER_IMAGE = "/placeholder.svg?height=300&width=300"
 
export default function DoctorsPage() {
 
  /* ================= FULL SEO ================= */
 
  const doctorsSchema = [
    {
      "@context": "https://schema.org",
      "@type": "MedicalOrganization",
      "name": "Pranaam Hospitals",
      "url": "https://pranaamhospitals.com/",
      "logo": "https://pranaamhospitals.com/wp-content/uploads/2025/logo.png",
      "description":
        "Pranaam Hospitals is a NABH-accredited multi-specialty hospital in Hyderabad with a team of highly experienced doctors and specialists.",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Hyderabad",
        "addressRegion": "Telangana",
        "addressCountry": "IN",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "https://pranaamhospitals.com/doctors",
      },
      "headline": "Meet Our Doctors at Pranaam Hospitals",
      "description":
        "Explore the profiles of expert doctors and medical specialists at Pranaam Hospitals providing patient-centric healthcare in Hyderabad.",
      "author": {
        "@type": "Organization",
        "name": "Pranaam Hospitals",
      },
      "publisher": {
        "@type": "Organization",
        "name": "Pranaam Hospitals",
        "logo": {
          "@type": "ImageObject",
          "url": "https://pranaamhospitals.com/wp-content/uploads/2025/logo.png",
        },
      },
      "datePublished": "2015-01-01",
      "dateModified": "2025-04-01",
    },
  ]
 
  /* ================= STATE ================= */
 
  const [doctors, setDoctors] = useState([])
  const [categories, setCategories] = useState([{ id: "all", name: "All Departments" }])
const [locations, setLocations] = useState([])
const [excellences, setExcellences] = useState([])
const [seo, setSeo] = useState({});
const [pageKeywords, setPageKeywords] = useState("");
 
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedDepartment, setSelectedDepartment] = useState("all")
  const [selectedLocation, setSelectedLocation] = useState("all")
  const [selectedExcellence, setSelectedExcellence] = useState([])
 
  const [excellenceSearch, setExcellenceSearch] = useState("")
  const [locationSearch, setLocationSearch] = useState("")
  const [departmentSearch, setDepartmentSearch] = useState("")
 
  const [visibleCount, setVisibleCount] = useState(INITIAL_LOAD)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)


  const handleExcellenceChange = (id) => {
    setSelectedExcellence((prev) =>
      prev.includes(id)
        ? prev.filter((item) => item !== id)
        : [...prev, id]
    )
  }
 
  const hasFetched = useRef(false)
 
  /* ================= FETCH CATEGORIES ================= */
 
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`/wp-json/wp/v2/services?per_page=100`)
        const data = await res.json()
           setCategories([
            { id: "all", name: "All Departments" },
            ...data.map((item) => ({
              id: item.id,
              name: item.name
            }))
          ])
      } catch (err) {
        console.error(err)
      }
    }
 
    fetchCategories()

    fetch("/wp-json/custom/v1/page-seo?slug=doctors")
      .then(res => res.json())
      .then(data => { if (data?.seo) setSeo(data.seo); setPageKeywords(data?.keywords || ""); })
      .catch(() => {})
  }, [])

    useEffect(() => {
          fetch('/wp-json/custom/v1/filters')
            .then(res => res.json())
            .then(data => {

              setLocations([
                { id: "all", name: "All Locations" },
                ...data.locations.map(loc => ({
                  id: loc,
                  name: loc
                }))
              ])

              setExcellences(
                data.excellence.map(ex => ({
                  id: ex.toLowerCase().replace(/\s+/g, "-"),
                  name: ex
                }))
              )
            })
        }, [])
 
  /* ================= FETCH DOCTORS ================= */
 
  useEffect(() => {
    if (hasFetched.current) return
    hasFetched.current = true
 
    const fetchDoctors = async () => {
      try {
       const res = await fetch(
  `${WP_API_BASE}/doctors?per_page=100&orderby=menu_order&order=asc&_embed=wp:featuredmedia`
)
        const data = await res.json()
 
      const mapped = data.map((post) => {
 
        // Centre of Excellence
        let doctorExcellence = post.acf?.centre_of_excellence || []
        if (typeof doctorExcellence === "string") {
          doctorExcellence = [doctorExcellence]
        }
 
        // Locations
        let doctorLocations = post.acf?.locations || []
        if (typeof doctorLocations === "string") {
          doctorLocations = [doctorLocations]
        }
       
       return {
        id: post.id,
        name: post.title?.rendered || "",
        slug: post.slug,
        designation: post.acf?.designation || "",
        qualifications: post.acf?.qualifications?.trim() || "",
        specialty: post.acf?.latest_achievement || "",
        serviceIds: post.services || [],
        locations: doctorLocations,
        excellence: doctorExcellence,
        image: post._embedded?.["wp:featuredmedia"]?.[0]?.source_url || PLACEHOLDER_IMAGE,

        // ✅ ADD THIS
        menu_order: post.menu_order || 1
       }

      })
 
      setDoctors(mapped)
        setLoading(false)
      } catch (err) {
        console.error(err)
        setLoading(false)
      }
 
     
    }
 
    fetchDoctors()
  }, [])
 
/* ================= FILTER ================= */
 
    const normalize = (str) =>
      str.toLowerCase().replace(/\s+/g, "-")
 
const filteredDoctors = doctors
  .filter((doctor) => {
    const doctorDepts = categories
      .filter(cat => doctor.serviceIds?.includes(cat.id))
      .map(cat => cat.name.toLowerCase());

    const matchesSearch =
      doctor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doctor.specialty.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doctor.designation.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doctorDepts.some(dept => dept.includes(searchQuery.toLowerCase()))

    const matchesDepartment =
      selectedDepartment === "all" ||
      doctor.serviceIds?.includes(Number(selectedDepartment))

    const matchesLocation =
      selectedLocation === "all" ||
      (Array.isArray(doctor.locations) &&
        doctor.locations.includes(selectedLocation))

    const matchesExcellence =
      selectedExcellence.length === 0 ||
      doctor.excellence?.some((ex) =>
        selectedExcellence.includes(normalize(ex))
      )

    return (
      matchesSearch &&
      matchesDepartment &&
      matchesLocation &&
      matchesExcellence
    )
  })

  // ✅ ADD THIS SORT HERE
  .sort((a, b) => {
    const orderA = Number(a.menu_order);
    const orderB = Number(b.menu_order);

    // push 0 to bottom
    if (orderA === 0 && orderB !== 0) return 1;
    if (orderA !== 0 && orderB === 0) return -1;

    // normal ascending order
    return orderA - orderB;
  });
  
 
 
    const visibleDoctors = filteredDoctors.slice(0, visibleCount)
 
  /* ================= LOAD MORE ================= */
 
  const handleLoadMore = () => {
    setLoadingMore(true)
    setTimeout(() => {
      setVisibleCount((prev) => prev + LOAD_MORE_COUNT)
      setLoadingMore(false)
    }, 500)
  }
 
  /* ================= LOADER ================= */
 
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary border-t-transparent"></div>
      </div>
    )
  }
 
  /* ================= RENDER ================= */
 
  return (
    <>
      <SEO
        title={seo.title || "Our Doctors | Expert Medical Specialists at Pranaam Hospitals Hyderabad"}
        description={seo.description || "Meet the experienced doctors and medical specialists at Pranaam Hospitals."}
        canonical={seo.canonical || "https://pranaamhospitals.com/doctors"}
        robots={Array.isArray(seo.robots) ? seo.robots.join(", ") : seo.robots || "index, follow"}
        keywords={pageKeywords}
        ogTitle={seo.og_title}
        ogDescription={seo.og_description}
        ogImage={seo.og_image || seo.featured_image}
        twitterTitle={seo.twitter_title}
        twitterDescription={seo.twitter_description}
        schema={doctorsSchema}
      />
 
       <main className="min-h-screen bg-white">        
 
          {/* Hero */}
          <section className="py-4 text-center">
            <h1 className="text-3xl font-bold text-primary">Doctors</h1>
          </section>
 
          {/* Search + Filters */}
          <section className="pb-4">
            <div className="container mx-auto px-4">
              <div className="bg-white rounded-xl shadow-sm border rounded-lg p-4 md:p-6 mb-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Search Doctor</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        placeholder="Search doctors..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 border border-gray-300"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select Department</label>
                    <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                      <SelectTrigger aria-label="Select Department">
                        <SelectValue placeholder="All Departments" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((dept) => (
                          <SelectItem key={dept.id} value={String(dept.id)}>{dept.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select Location</label>
                    <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                      <SelectTrigger aria-label="Select Location">
                        <SelectValue placeholder="All Locations" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map((loc) => (
                          <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Centre of Excellence</label>
                    <Select value={selectedExcellence.length === 1 ? selectedExcellence[0] : "all"} onValueChange={(val) => setSelectedExcellence(val === "all" ? [] : [val])}>
                      <SelectTrigger aria-label="Select Centre of Excellence">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        {excellences.map((item) => (
                          <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div>
 
 
                  {/* Doctors Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                    {visibleDoctors.map((doctor) => (
                      <div key={doctor.id} className="p-4 border rounded-lg text-center flex flex-col h-full">
                        <div className="w-full mb-4 overflow-hidden rounded-lg">
                          <img
                              src={doctor.image}
                              alt={doctor.name}
                              width="400"
                              height="400"
                              loading="lazy"
                              className="w-full h-auto object-contain"
                            />
                        </div>
                        <h2 className="text-xl font-bold text-primary">{doctor.name}</h2>
                        <p className="text-sm text-gray-700">{doctor.designation}</p>
                        <div className="mt-auto pt-3">
                          <Button asChild className="w-full bg-primary hover:bg-primary/90">
                            <Link to={`/doctors/${doctor.slug}`}>View Profile</Link>
                          </Button>
                        </div>
                      </div>
                    ))}
 
                      {visibleDoctors.length === 0 && (
                        <p className="text-center text-gray-500 col-span-4">
                          No doctors found for selected filters
                        </p>
                      )}
                    {loadingMore && [...Array(3)].map((_, i) => (
                      <div key={i} className="h-80 bg-gray-200 rounded-lg animate-pulse" />
                    ))}
                  </div>
 
                  {visibleCount < filteredDoctors.length && (
                    <div className="text-center mt-12">
                      <Button size="lg" variant="outline" disabled={loadingMore} onClick={handleLoadMore}>
                        {loadingMore ? "Loading..." : "Load More Doctors"}
                      </Button>
                    </div>
                  )}
                </div>
            </div>
          </section>
 
        
      </main>
    </>
  )
}